import React, { useState } from 'react';
import { Mail, Loader2 } from 'lucide-react';
import { logger } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact newsletter capture form.
 * Props:
 *  - source: string ("footer" | "blog" | "article-canicule" …)
 *  - title:  string (heading shown above the form)
 *  - subtitle: string (optional small text under title)
 *  - compact: bool (if true, use a smaller layout)
 */
const NewsletterSignup = ({
  source = 'footer',
  title = 'Recevez nos conseils nautiques',
  subtitle = 'Un email par mois maximum. Pas de spam, jamais.',
  compact = false,
}) => {
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // {ok, msg}

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), source, website }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur');
      setResult({ ok: true, msg: data.message });
      setEmail('');
    } catch (err) {
      logger.warn('Newsletter subscribe error', err);
      setResult({ ok: false, msg: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`${
        compact
          ? 'bg-[#1a1c1b] border border-[#3f4816] rounded-xl p-4'
          : 'bg-gradient-to-br from-[#302f2c] to-[#1a1c1b] border border-[#3f4816] rounded-2xl p-6 md:p-8'
      }`}
      data-testid={`newsletter-${source}`}
    >
      {title && (
        <h3 className={`text-[#d9fb06] font-bold uppercase ${compact ? 'text-base mb-1' : 'text-xl md:text-2xl mb-2'}`}>
          {title}
        </h3>
      )}
      {subtitle && (
        <p className={`text-[#888680] ${compact ? 'text-xs mb-3' : 'text-sm mb-4'}`}>
          {subtitle}
        </p>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
        {/* honeypot */}
        <input type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)}
          className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
        <div className="relative flex-1">
          <Mail
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888680] pointer-events-none"
          />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.fr"
            className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-full pl-9 pr-4 py-2.5 text-sm text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
            data-testid="newsletter-email-input"
            disabled={submitting}
          />
        </div>
        <button
          type="submit"
          disabled={submitting || !email.trim()}
          className="bg-[#d9fb06] text-[#1a1c1b] font-bold rounded-full px-5 py-2.5 text-sm uppercase hover:bg-[#c4e205] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="newsletter-submit-btn"
        >
          {submitting ? (
            <>
              <Loader2 size={14} className="animate-spin" />
              Envoi…
            </>
          ) : (
            "S'inscrire"
          )}
        </button>
      </form>
      {result && (
        <p
          className={`mt-3 text-sm ${result.ok ? 'text-[#d9fb06]' : 'text-red-400'}`}
          data-testid="newsletter-feedback"
        >
          {result.msg}
        </p>
      )}
    </div>
  );
};

export default NewsletterSignup;
